import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  LinearProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import CommonPagination from "../../components/Table/CommonPagination";
import { getSearchingPosts } from "../../http/inProgressMatches";
import CompetingOnCell from "./CompetingOnCell";
import {
  RANK_ORDER,
  RANK_COLORS,
  NO_RANK_LABEL,
  ROWS_PER_PAGE,
  formatAge,
  ageColor,
  formatMatchType,
  rankChipSx,
  filterSelectSx,
  useSectionStyles,
} from "./matchDisplay";

const POST_TYPES = [
  "PROMPT_POST",
  "CATEGORY_POST",
  "CONTEST_POST",
  "KARAOKE_POST",
  "DANCE_POST",
  "ART_POST",
  "GROUP_DUET_POST",
];

/**
 * Embeddable Searching Posts section (summary, filters, paginated list).
 * The matchmaking pool: posts still competing but not currently in any active
 * match — what their owners see as "Searching for pagent" in the app. Waiting
 * time counts from the post's last completed match (or from posting).
 * Rendered on the Match & Energy page below the Active Matches section.
 */
const SearchingPostsSection = () => {
  const classes = useSectionStyles();

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total: 0, byRank: [], byAge: [] });
  const [posts, setPosts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rankFilter, setRankFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("oldest");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSearchingPosts({
        page: page + 1,
        limit: ROWS_PER_PAGE,
        rank: rankFilter,
        postType: typeFilter,
        sortOrder,
      });
      const data = res?.data || {};
      setSummary(data.summary || { total: 0, byRank: [], byAge: [] });
      setPosts(data.posts || []);
      setTotalCount(data.pagination?.total || 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [page, rankFilter, typeFilter, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  // Zero-fill rank rows so every tier is always visible.
  const byRankMap = new Map((summary.byRank || []).map((r) => [r.rank, r]));
  const rankRows = RANK_ORDER.map(
    (rank) =>
      byRankMap.get(rank) || {
        rank,
        count: 0,
        avgAgeMs: null,
        oldestAgeMs: null,
      }
  );
  const maxRankCount = Math.max(1, ...rankRows.map((r) => r.count));

  const ageRows = summary.byAge || [];
  const maxAgeCount = Math.max(1, ...ageRows.map((r) => r.count));

  const percentOfTotal = (count) =>
    summary.total > 0 ? Math.round((count / summary.total) * 100) : 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#142C5C",
              fontSize: { xs: "18px", sm: "20px", md: "22px" },
              lineHeight: 1.3,
            }}
          >
            Searching for Pagent
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, fontSize: "13px" }}
          >
            The matchmaking pool: posts still competing that are waiting for an
            opponent. Waiting time counts from the post&apos;s last completed
            match, or from when it was posted.
          </Typography>
        </Box>
        <Button
          variant="contained"
          className={classes.refreshButton}
          startIcon={<RefreshIcon />}
          onClick={fetchData}
        >
          Refresh
        </Button>
      </Box>

      {loading && (
        <LinearProgress sx={{ marginBottom: "16px", borderRadius: 2 }} />
      )}

      {/* Summary: rank breakdown + wait-time distribution */}
      <Box
        sx={{
          display: "flex",
          gap: "24px",
          flexWrap: "wrap",
          marginBottom: "24px",
        }}
      >
        <Box className={classes.summaryCard} sx={{ flex: "1 1 420px", minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}
          >
            By Rank ({summary.total} searching)
          </Typography>
          {rankRows.map((row) => (
            <Box key={row.rank} sx={{ marginBottom: "10px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "3px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <Chip label={row.rank} size="small" sx={rankChipSx(row.rank)} />
                  <Typography sx={{ fontSize: "12px", color: "#666" }}>
                    {row.count} ({percentOfTotal(row.count)}%)
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: "11px", color: "#888", whiteSpace: "nowrap" }}
                >
                  {row.count > 0
                    ? `avg wait ${formatAge(row.avgAgeMs)} · longest ${formatAge(
                        row.oldestAgeMs
                      )}`
                    : "—"}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(row.count / maxRankCount) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor:
                      RANK_COLORS[row.rank] || RANK_COLORS[NO_RANK_LABEL],
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        <Box className={classes.summaryCard} sx={{ flex: "1 1 320px", minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: "16px", fontWeight: 600, marginBottom: "16px" }}
          >
            By Wait Time
          </Typography>
          {ageRows.map((row) => (
            <Box key={row.bucket} sx={{ marginBottom: "10px" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "3px",
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: "#333" }}
                >
                  {row.bucket}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#666" }}>
                  {row.count} ({percentOfTotal(row.count)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(row.count / maxAgeCount) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    backgroundColor: "#142C5C",
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Post list */}
      <Box className={classes.listSection}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: 600 }}>
            Posts ({totalCount})
          </Typography>
          <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              label="Rank"
              value={rankFilter}
              onChange={handleFilterChange(setRankFilter)}
              sx={filterSelectSx}
            >
              <MenuItem value="">All ranks</MenuItem>
              {RANK_ORDER.map((rank) => (
                <MenuItem key={rank} value={rank}>
                  {rank}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Post Type"
              value={typeFilter}
              onChange={handleFilterChange(setTypeFilter)}
              sx={filterSelectSx}
            >
              <MenuItem value="">All types</MenuItem>
              {POST_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {formatMatchType(type)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Sort"
              value={sortOrder}
              onChange={handleFilterChange(setSortOrder)}
              sx={filterSelectSx}
            >
              <MenuItem value="oldest">Longest waiting first</MenuItem>
              <MenuItem value="newest">Shortest waiting first</MenuItem>
            </TextField>
          </Box>
        </Box>

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow className={classes.stickyHeader}>
                <TableCell className={classes.headerCell}>Post ID</TableCell>
                <TableCell className={classes.headerCell}>Player</TableCell>
                <TableCell className={classes.headerCell}>Type</TableCell>
                <TableCell className={classes.headerCell}>Competing On</TableCell>
                <TableCell className={classes.headerCell}>Rank</TableCell>
                <TableCell className={classes.headerCell}>Matches Played</TableCell>
                <TableCell className={classes.headerCell}>Waiting</TableCell>
                <TableCell className={classes.headerCell}>Posted</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    sx={{
                      textAlign: "center",
                      height: "160px",
                      fontSize: "14px",
                      color: "#999",
                    }}
                  >
                    No posts are currently searching for a pagent.
                  </TableCell>
                </TableRow>
              ) : (
                posts.map((post) => (
                  <TableRow key={post._id}>
                    <TableCell className={classes.bodyCell}>
                      <span className={classes.idText}>{post._id}</span>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <span>{post.player || "—"}</span>
                      {post.isGroup && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "11px",
                            color: "#888",
                            marginLeft: "4px",
                          }}
                        >
                          (group)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {formatMatchType(post.postType)}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <CompetingOnCell
                        song={post.song}
                        challenge={post.challenge}
                        contest={post.contest}
                        missionNumber={post.missionNumber}
                        category={post.category}
                        isGroupDuet={post.isGroup}
                        groupDuetTargetType={post.groupDuetTargetType}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Chip
                        label={post.rank}
                        size="small"
                        sx={rankChipSx(post.rank)}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Typography
                        component="span"
                        sx={{ fontWeight: 600, fontSize: "13px" }}
                      >
                        {post.matchesPlayed}
                        {post.maxMatches ? ` / ${post.maxMatches}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: ageColor(post.ageMs),
                        }}
                      >
                        {formatAge(post.ageMs)}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {post.createdAt
                        ? dayjs(post.createdAt).format("MMM D, YYYY h:mm A")
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalCount > 0 && (
          <Box sx={{ marginTop: "12px" }}>
            <CommonPagination
              rowsPerPage={ROWS_PER_PAGE}
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SearchingPostsSection;
